<template>
  <Transition name="slide-fade">
    <ChartCard v-if="selectedStage" class="requisition-report-active-candidates">
      <RequisitionReportCandidateTableHeader
        :selected-stage="selectedStage"
        :candidate-stage-options="candidateStageOptions"
        :active-candidates="activeCandidates"
        :project-name="projectName"
        @set-candidate-stage="($event) => $emit('set-candidate-stage', $event)"
      />
      <div v-if="!candidatesLoading" class="w-100 d-flex flex-column pa-4">
        <div class="grid-container">
          <div class="grid-header">
            <div
              v-for="column in columns"
              :key="column.key"
              class="grid-header-cell new-h3"
              :class="{ 'cursor-pointer': column.canSort, 'ats-link': column.key === 'ats_link' }"
              @click="column.canSort && handleSort(column.key)"
            >
              <span>{{ column.label }}</span>
              <SortArrow
                v-if="column.canSort"
                :current-sort="column.key"
                :selected-sort="sortRootWord"
                :down-arrow-sort-direction="column.downArrowSortDirection"
                :selected-sort-direction="candidatesSortDirection"
              />
            </div>
          </div>
          <div
            v-for="candidate in activeCandidates[selectedStage].items"
            :key="candidate.candidate_uuid"
            class="grid-row"
          >
            <div class="grid-cell new-h3">
              {{ candidate.first_name }}
            </div>
            <div class="grid-cell new-body-text">
              <ATSUrl :url="candidate.candidate_ats_url" @track-ats-click="handleAtsUrlClick" />
            </div>
            <div class="grid-cell new-body-text">
              {{ formatApplicationDate(candidate.applied_at) }}
            </div>
            <div class="grid-cell new-body-text">
              <DpplTooltip location="start" :activator-props="{ style: 'max-width: 100%;' }">
                <template #activator="{ props: tooltipProps }">
                  <p v-bind="tooltipProps" class="ats-stage new-body-text">
                    {{ candidate.ats_stage_label }}
                  </p>
                </template>
                <p>
                  {{ candidate.ats_stage_label }}
                </p>
              </DpplTooltip>
            </div>
            <div class="grid-cell new-body-text">
              {{ getLatestActivityText(candidate.days_since_last_activity) }}
            </div>
            <div class="grid-cell new-body-text">
              {{ getTimeInProcessText(candidate.days_since_applied) }}
            </div>
          </div>
        </div>
        <div>
          <v-btn
            v-if="canLoadMore && !candidatesLoadingMore"
            variant="flat"
            size="small"
            rounded="xl"
            color="brandPrimary"
            class="mt-6 load-more-btn"
            @click="() => loadMoreActiveCandidatesByStage(selectedStage)"
          >
            Load More
          </v-btn>
        </div>
        <div v-if="candidatesLoadingMore" class="small-loader-container my-2">
          <v-progress-circular
            indeterminate
            color="brandPrimary"
            size="40"
          />
        </div>
      </div>
      <div v-else class="loader-container">
        <v-progress-circular
          indeterminate
          color="brandPrimary"
          size="40"
        />
      </div>
    </ChartCard>
  </Transition>
</template>

<script setup>
import { useRequisitionReport } from '@/stores/requisitions/requisitionReport';
import { storeToRefs } from 'pinia';
import RequisitionReportCandidateTableHeader from '@/components/requisitions/RequisitionReportCandidateTableHeader.vue';
import { formatDate } from '@/helpers/date';
import { computed } from 'vue';
import SortArrow from '@/components/shared/SortArrow.vue';
import DpplTooltip from '@/components/datapeople/DpplTooltip.vue';
import { useTracking } from '@/stores/tracking/tracking';
import { useRoute } from 'vue-router';
import ATSUrl from '@/components/shared/ATSUrl.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';

const route = useRoute();

const props = defineProps({
  selectedStage: { type: String, default: '' },
  candidateStageOptions: { type: Array, default: () => [] },
  projectName: { type: String, required: true },
});

defineEmits(['set-candidate-stage']);

const {
  activeCandidates,
  candidatesLoading,
  candidatesLoadingMore,
  candidatesSortDirection,
  candidatesSort,
} = storeToRefs(useRequisitionReport());
const { loadMoreActiveCandidatesByStage, getActiveCandidatesByStage } = useRequisitionReport();

const columns = [
  {
    key: 'first_name', label: 'Candidate First Name', space: 8, canSort: true, downArrowSortDirection: 'asc',
  },
  {
    key: 'ats_link', label: 'ATS Link', space: 36,
  },
  {
    key: 'applied_at', label: 'Application Date', space: 36, canSort: true, downArrowSortDirection: 'desc',
  },
  {
    key: 'ats_stage_label', label: 'Current ATS Stage', space: 36, canSort: true, downArrowSortDirection: 'asc',
  },
  {
    key: 'days_since_last_activity', label: 'Latest Activity', space: 36, canSort: true, downArrowSortDirection: 'asc',
  },
  {
    key: 'days_since_applied', label: 'Time In Process', space: 0, canSort: true, downArrowSortDirection: 'asc',
  },
];

const formatApplicationDate = (date) => {
  if (!date) return '';

  return formatDate(date, 'MM/dd/yyyy');
};

const totalPages = computed(() => activeCandidates.value[props.selectedStage].totalPages);
const lastFetchedPage = computed(() => activeCandidates.value[props.selectedStage].lastFetchedPage);

const canLoadMore = computed(() => totalPages.value > lastFetchedPage.value);
const sortRootWord = computed(() => (candidatesSort.value.startsWith('-') ? candidatesSort.value.slice(1) : candidatesSort.value));

const handleSort = async (sort) => {
  if (sortRootWord.value === sort) {
    if (candidatesSort.value.startsWith('-')) {
      await getActiveCandidatesByStage(props.selectedStage, sortRootWord.value);
    } else {
      await getActiveCandidatesByStage(props.selectedStage, `-${sortRootWord.value}`);
    }
  } else {
    const defaultPrepend = ['applied_at'].includes(sort) ? '-' : '';

    await getActiveCandidatesByStage(props.selectedStage, `${defaultPrepend}${sort}`);
  }
};

const handleAtsUrlClick = (url) => {
  useTracking().track({
    name: 'Requisition Report: Clicked Candidate URL in Active Candidate Table',
    params: {
      candidateUrl: url,
      requisitionUlid: route.params.id,
    },
  });
};

const getTimeInProcessText = (days) => {
  if (typeof days !== 'number') return '';
  if (days === 1) return '1 Day';

  return `${days} Days`;
};

const getLatestActivityText = (days) => {
  if (typeof days !== 'number') return '';
  if (days === 0) return 'Today';
  if (days === 1) return '1 Day';

  return `${days} Days`;
};
</script>

<style scoped lang="less">
@import '@/less/mixins';
.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.requisition-report-active-candidates {
  width: 100%;
  font-size: 14px;
}

.grid-container {
  display: grid;
  grid-template-columns: 200px 85px 160px minmax(0, 1fr) 130px 150px;
  width: 100%;
}

.grid-header {
  display: contents;
}

.grid-header-cell {
  text-align: left;
  border-bottom: 1px solid @stroke-default;
  padding: 8px 0px 8px 8px;

  &.ats-link {
    text-align: center;
  }
}

.grid-row {
  display: contents;
}

.grid-cell {
  text-align: left;
  border-bottom: 1px solid @stroke-default;
  padding: 8px 0px 8px 8px;
}

.loader-container {
  width: 100%;
  min-height: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.small-loader-container {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.load-more-btn :deep(.v-btn__content) {
  text-transform: none;
}

.candidates-header:hover :deep(.sort-arrow):not(.active) {
  opacity: .5;
}

.ats-stage {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
